import { useMemo } from 'react';

import Grid from '@mui/material/Grid';
import { useFormContext, useWatch } from 'react-hook-form';

import type {
  Teams_Bool_Exp,
  Users_Bool_Exp,
} from '@graphql/__generated__/graphql';
import type { TransactionFormData } from '@modules/_shared/components/create-transaction/hooks/useTransactionStepper';
import { RaDate } from '@modules/_shared/components/form/RaDate';
import { RaNumber } from '@modules/_shared/components/form/RaNumber';
import { RaTeam } from '@modules/_shared/components/form/RaTeam';
import { RaUser } from '@modules/_shared/components/form/RaUser';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { isSellerTeamLocked } from '@modules/_shared/utils/transactionHelpers';
import { useLocale } from '@shared/hooks/locale';

import { useTransactionFormUi } from '../TransactionFormUi';
import { TransactionStepShell } from '../TransactionStepShell';

export const DetailsStep = () => {
  const { t } = useLocale();
  const { me } = useAppData();
  const { control } = useFormContext<TransactionFormData>();

  const selectedTeamId = useWatch({
    control,
    name: 'details.seller_broker_team_id',
  });

  const isAdmin = me?.is_admin ?? false;

  const alignedTeamIds = useMemo(
    () => me?.teams?.map(team => team.team_id) ?? [],
    [me?.teams],
  );

  const teamFilters: Teams_Bool_Exp | undefined = useMemo(
    () => (isAdmin ? undefined : { id: { _in: alignedTeamIds } }),
    [isAdmin, alignedTeamIds],
  );

  const isOnboarding = useTransactionFormUi() === 'onboarding';

  const agentFilters: Users_Bool_Exp = useMemo(
    () => ({
      is_broker: { _eq: true },
      ...(selectedTeamId &&
        !isOnboarding && {
          teams_users: { team_id: { _eq: selectedTeamId } },
        }),
    }),
    [selectedTeamId, isOnboarding],
  );

  return (
    <TransactionStepShell title={t('Sale details')} withFieldsGrid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <RaNumber
          name="details.purchase_price"
          label={t('Sale price')}
          control={control}
          required
          decimalNumbers={0}
          min={1}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <RaDate
          name="details.signature_date"
          label={t('Transaction Signature date')}
          control={control}
          required
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <RaUser
          name="details.seller_broker_id"
          label={t('Agent')}
          control={control}
          required={!isOnboarding}
          filters={agentFilters}
          shortlistTeamId={selectedTeamId}
          disabled={isOnboarding}
          openOnFocus={!isOnboarding && selectedTeamId != null}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <RaTeam
          name="details.seller_broker_team_id"
          label={t('Agency')}
          control={control}
          filters={teamFilters}
          disabled={isSellerTeamLocked(me)}
        />
      </Grid>
    </TransactionStepShell>
  );
};
